import type { IFilter, ISectionedFilters } from "types/filters";

import { Map } from "immutable";
import { IntegrationModel } from "models/IntegrationModel";
import { TicketModel, TicketStatus } from "models/TicketModel";
import { UserModel } from "models/UserModel";
import { apiReqAbortOld } from "utils/api/apiReqAbortOld";
import { paginatedSearchOptionsToBody } from "utils/api/urlSearchParams";
import { type IPaginationResponse, withPagination } from "utils/pagination";
import { type IPaginatedSearchOptions } from "utils/searchUtils";

import { apiReq } from "../utils/api/apiReq";

interface IProfileData {
  integrationActors?: string[];
}

export async function changeProfile(creationData: IProfileData): Promise<UserModel> {
  const { data } = await apiReq("PUT", "/v1/user", creationData);
  return UserModel.fromServerData(data);
}

export async function getMyPendingTickets(
  paginationOptions?: IPaginatedSearchOptions,
): Promise<IPaginationResponse<TicketModel>> {
  const body: Record<string, unknown> = {
    ...paginatedSearchOptionsToBody(paginationOptions),
    filters: {
      ticketStatus: {
        operator: "is",
        value: [TicketStatus.waitingForApproval, TicketStatus.permissionInProgress, TicketStatus.waitingForIT],
      },
    },
  };

  return withPagination(
    (ticket) => TicketModel.fromServerData(ticket),
    () => apiReq("POST", "/v1/user/tickets/myPending/search", body),
  );
}

export async function getUser() {
  const { data } = await apiReq("GET", "/v1/user");
  return UserModel.fromServerData(data);
}

export async function getUserAllPastTickets(
  paginationOptions?: IPaginatedSearchOptions,
  options?: { filters?: IFilter[] | ISectionedFilters },
  abortKey: string = "getUserAllPastTickets",
): Promise<IPaginationResponse<TicketModel>> {
  const body = paginatedSearchOptionsToBody(paginationOptions, options?.filters);
  body.includeOwnRequests = true;
  body.includeRespondedTo = true;

  return withPagination(
    (ticket) => TicketModel.fromServerData(ticket),
    () =>
      apiReqAbortOld({
        body,
        key: `${abortKey}${paginationOptions?.pagination?.page ?? "1"}`,
        method: "POST",
        path: "/v1/user/tickets/past/search",
      }),
  );
}

export async function getUserIntegrationsStatistics(): Promise<Map<string, IntegrationModel>> {
  const { data } = await apiReq("GET", "/v1/user/integrationsStatistics");
  return Map<string, IntegrationModel>(
    data
      .map((integration: unknown) => IntegrationModel.fromServerData(integration))
      .map((integration: IntegrationModel) => [integration.id, integration]),
  );
}

export async function getUserPastTickets(
  paginationOptions?: IPaginatedSearchOptions,
  options?: { filters?: IFilter[] | ISectionedFilters },
  abortKey: string = "getUserPastTickets",
): Promise<IPaginationResponse<TicketModel>> {
  const body = paginatedSearchOptionsToBody(paginationOptions, options?.filters);
  body.includeOwnRequests = true;

  return withPagination(
    (ticket) => TicketModel.fromServerData(ticket),
    () =>
      apiReqAbortOld({
        body,
        key: `${abortKey}${paginationOptions?.pagination?.page ?? "1"}`,
        method: "POST",
        path: "/v1/user/tickets/past/search",
      }),
  );
}

export async function getUserPendingTickets(
  paginationOptions?: IPaginatedSearchOptions,
): Promise<IPaginationResponse<TicketModel>> {
  const body: Record<string, unknown> = paginatedSearchOptionsToBody(paginationOptions);

  return withPagination(
    (ticket) => TicketModel.fromServerData(ticket),
    () => apiReq("POST", "/v1/user/tickets/pendingApproval/search", body),
  );
}

export async function getUserRespondedTickets(
  paginationOptions?: IPaginatedSearchOptions,
  options?: { filters?: IFilter[] | ISectionedFilters },
  abortKey: string = "getUserRespondedTickets",
): Promise<IPaginationResponse<TicketModel>> {
  const body = paginatedSearchOptionsToBody(paginationOptions, options?.filters);
  body.includeRespondedTo = true;

  return withPagination(
    (ticket) => TicketModel.fromServerData(ticket),
    () =>
      apiReqAbortOld({
        body,
        key: `${abortKey}${paginationOptions?.pagination?.page ?? "1"}`,
        method: "POST",
        path: "/v1/user/tickets/past/search",
      }),
  );
}

export async function registerUserToSlack(slackUserId: string): Promise<boolean> {
  const { data } = await apiReq<{ ok: boolean }>("POST", `/v1/user/registerToSlack/${slackUserId}`);
  return data.ok;
}
